import React from 'react';
import { useIntl } from 'react-intl';
import JobEbpSpotlightComponent, {
  JobEbpSpotlightButton,
} from '@fuww/library/src/JobEbpSpotlight';
import messages from '../../../lib/messages.json';
import LinkAndAnchor from '../../LinkAndAnchor';
import { useSiteContext } from '../../SiteContext';
import getEbpSpotlightProfile from '../../../lib/getEbpSpotlightProfile';
import ImageWithUrls from '../../ImageWithUrls';
import getSourceSet from '../../../lib/getSourceSet';
import {
  mobileSpotlightImageConfigurations,
} from '../../../lib/imageConfigurations';
import { JobQuery } from '../../../lib/graphql/api/graphql';

const JobEbpSpotlight = ({ job }: { job?: JobQuery['job'] }) => {
  const intl = useIntl();
  const {
    locale,
    jobsComplete,
    isCareerPage,
  } = useSiteContext();

  if (!job) return null;

  const {
    company,
    profiles,
  } = job;
  const {
    displayName,
  } = company;

  const ebpSpotlightProfile = getEbpSpotlightProfile<
    NonNullable<JobQuery['job']>['profiles'][number]
  >(company, profiles);

  if (!ebpSpotlightProfile) {
    return null;
  }

  const {
    page: {
      imageUrls,
      mobileImageUrls,
      slug: ebpSpotlightSlug,
    },
  } = ebpSpotlightProfile;

  const mobileSpotlightImageUrls = mobileImageUrls[0] === null
    ? imageUrls.slice(0, 3)
    : mobileImageUrls.slice(0, 3);
  const mobileSourceSet = getSourceSet(
    mobileSpotlightImageConfigurations,
    mobileSpotlightImageUrls,
  );

  const tabletSpotlightImageUrls = imageUrls.slice(3, 6);
  const tabletSourceSet = getSourceSet(
    [{ width: 42 }, { width: 720 }, { width: 1440 }],
    tabletSpotlightImageUrls,
  );

  const desktopSpotlightImageUrls = mobileImageUrls[0] === null
    ? imageUrls.slice(6)
    : mobileImageUrls.slice(3);
  const desktopSourceSet = getSourceSet(
    [{ width: 42 }, { width: 320 }, { width: 640 }],
    desktopSpotlightImageUrls,
  );

  return (
    <JobEbpSpotlightComponent
      ebpLink={(
        <LinkAndAnchor
          route={isCareerPage ? `careers.${locale}` : `jobs.${locale}`}
          params={{ companySlug: ebpSpotlightSlug }}
          title={intl.formatMessage(messages['jobView.ebpLink'])}
          useRouter={jobsComplete}
        >
          <JobEbpSpotlightButton>
            {intl.formatMessage(messages['jobView.ebpLink'])}
          </JobEbpSpotlightButton>
        </LinkAndAnchor>
      )}
      epbCallout={intl.formatMessage(
        messages['jobView.ebpCallout'],
        { companyName: displayName },
      )}
      mobileImage={(
        mobileSpotlightImageUrls.every((url) => typeof url === 'string')
          ? (
            <ImageWithUrls
              imageUrls={mobileSpotlightImageUrls as string[]}
              sizes="calc(100vw - 48px)"
              srcSet={mobileSourceSet}
              alt={displayName}
            />
          ) : null
      )}
      tabletImage={(
        tabletSpotlightImageUrls.every((url) => typeof url === 'string')
          ? (
            <ImageWithUrls
              imageUrls={tabletSpotlightImageUrls as string[]}
              sizes="calc(100vw - 48px)"
              srcSet={tabletSourceSet}
              alt={displayName}
            />
          ) : null
      )}
      desktopImage={(
        desktopSpotlightImageUrls.every((url) => typeof url === 'string')
          ? (
            <ImageWithUrls
              imageUrls={desktopSpotlightImageUrls as string[]}
              sizes="320px"
              srcSet={desktopSourceSet}
              alt={displayName}
              height="256px"
            />
          ) : null
      )}
    />
  );
};

export default JobEbpSpotlight;
